import { Box, Heading, useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout } from 'components/atoms'
import { InputBuild, MultiSelectBuild, SelectBuild } from 'components/molecules'
import { FileGeneratorMultiPDF } from 'components/molecules/FileGeneratorMultiPDF'
import { useAuthContext } from 'contexts/auth'
import { useInstitutionsContext } from 'contexts/institutions'
import { motion } from 'framer-motion'
import jwt_decode from 'jwt-decode'
import { AuthTokens } from 'models'
import { ReportQueryDTO } from 'models/ReportQueryDTO'
import { FormReportProps } from 'pages/Report/interface'
import { BottonLine, FormContainer } from 'pages/Report/styles'
import { FileItem } from 'pages/Report/types'
import { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { getCSVOperationalVisionReports } from 'services/report'
import { generateFileUTF } from 'utils/blob'
import { removeEmptyValues } from 'utils/removeEmptyValues'
import { FormReportValue } from './form'
import { schema } from './schema'

export const RevenueReport: React.FC = () => {
    const {
        register,
        formState: { errors },
        watch,
        control
    } = useForm<FormReportProps>({
        resolver: yupResolver(schema),
        mode: 'onChange'
    })

    const { isBackoffice, currentUser } = useAuthContext()
    const { currentInstitution, currentAgent } = useInstitutionsContext()

    const [loadingReport, setLoadingReport] = useState<boolean>(false)

    const toast = useToast()

    const decodedIdToken: AuthTokens = jwt_decode(
        currentUser?.idToken as string
    )

    const all = watch()

    const callAPI = (file: FileItem) => {
        const queryRemoveEmptyValues = removeEmptyValues({
            ...all,
            dataInicio: all.dataInicio ? (all.dataInicio as string) : '',
            dataFinal: all.dataFinal ? (all.dataFinal as string) : '',
            status: all.status === 'TODOS' ? '' : all.status,
            operacao: all.operacao === 'TODOS' ? '' : all.operacao,
            cnpjAgente: currentAgent?.cnpj ?? '',
            fatura: file.faturaValue
        })
        const query = {
            ...queryRemoveEmptyValues,
            tipo: file.type
        }
        downloadFiles(file, query)
    }

    const downloadFiles = async (file: FileItem, query: ReportQueryDTO) => {
        setLoadingReport(true)

        try {
            const res = await getCSVOperationalVisionReports(
                query,
                isBackoffice,
                currentInstitution?.id,
                file.newLayout
            )

            generateFileUTF(file.type, res)

            toast({
                title: 'Arquivo gerado com sucesso!',
                description: `O arquivo ${file.type} foi gerado conforme os filtros selecionados. Verifique sua pasta de downloads para acessá-lo.`,
                position: 'bottom-left',
                status: 'success',
                isClosable: true
            })
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description: 'Ocorreu um erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoadingReport(false)
        }
    }

    const form = useMemo(() => FormReportValue(decodedIdToken?.UFDetran), [])

    return (
        <Box
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                paddingBottom="24px"
            >
                <Heading size="md">Relatórios</Heading>
                <FileGeneratorMultiPDF
                    callAPI={callAPI}
                    loading={loadingReport}
                />
            </Box>
            <form>
                <Box
                    paddingTop="24px"
                    bgColor="white"
                    borderRadius={6}
                    h="100%"
                >
                    <Layout type="container" px={8} full display="grid" gap={8}>
                        <FormContainer
                            minH={{
                                base: 'calc(100vh - 310px)',
                                md: 'calc(100vh - 260px)'
                            }}
                        >
                            <Layout type="row" gutter={{ y: 1.5 }}>
                                {form.map(
                                    (
                                        { layout, input, select, multiSelect },
                                        key
                                    ) => (
                                        <Layout {...layout} key={key}>
                                            {multiSelect ? (
                                                <Controller
                                                    name={multiSelect.id}
                                                    control={control}
                                                    defaultValue={
                                                        multiSelect.defaultValue ||
                                                        ''
                                                    }
                                                    render={({ field }) => (
                                                        <MultiSelectBuild
                                                            {...multiSelect}
                                                            value={
                                                                field.value ||
                                                                ''
                                                            }
                                                            onChange={
                                                                field.onChange
                                                            }
                                                            onBlur={
                                                                field.onBlur
                                                            }
                                                            err={
                                                                errors[
                                                                    multiSelect
                                                                        .id
                                                                ] as any
                                                            }
                                                        />
                                                    )}
                                                />
                                            ) : select ? (
                                                <SelectBuild
                                                    {...select}
                                                    err={errors[select.id]}
                                                    {...register(select.id)}
                                                />
                                            ) : (
                                                input && (
                                                    <InputBuild
                                                        {...input}
                                                        err={errors[input.id]}
                                                        {...register(input.id)}
                                                        value={watch(input.id)}
                                                    />
                                                )
                                            )}
                                        </Layout>
                                    )
                                )}
                                <Box>
                                    <BottonLine />
                                </Box>
                            </Layout>
                        </FormContainer>
                    </Layout>
                </Box>
            </form>
        </Box>
    )
}
